"use client";

import { useCallback, useEffect, useState } from "react";
import KwikPassScript from "./KwikPassScript";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { dispatchOtherPageViewEvent } from "./KwikpassEvent";
import { CartStorage } from "@/libs/cart-storage";
import {
  decodeKpToken,
  getCustomerAccessToken,
  getCustomerAccessTokenMultipass,
  getCustomerAccessTokenByActivation,
} from "./AuthUtils";
import { EventType, publishEvent } from "@/events";

// Extend Window interface to include KwikPass event handlers
declare global {
  interface Window {
    handleCustomLogin?: (param: boolean) => void;
  }
}

// Define custom event types for KwikPass
type KwikPassLoginEvent = CustomEvent<{
  [key: string]: unknown;
}>;

// Extend WindowEventMap to include KwikPass events
declare global {
  interface WindowEventMap {
    kp_data_sent: KwikPassLoginEvent;
  }
}

export default function KwikPass() {
  const pathname = usePathname();
  const [isKwikPassScriptLoaded, setIsKwikPassScriptLoaded] = useState(false);
  const { login } = useAuth();

  const onLoad = () => {
    setIsKwikPassScriptLoaded(true);
  };

  const handleUserLogin = useCallback(
    async (event: KwikPassLoginEvent) => {
      if (event?.detail?.kpToken) {
        try {
          const customer = await decodeKpToken(event.detail.kpToken as string);

          let accessToken: string | null = null;

          if (customer) {
            if (customer?.email && customer?.password) {
              accessToken = await getCustomerAccessToken(customer);
            }

            if (customer?.multipassToken) {
              accessToken = await getCustomerAccessTokenMultipass(customer);
            }

            if (customer?.accountActivationUrl) {
              accessToken = await getCustomerAccessTokenByActivation(customer);
            }
          }

          if (accessToken) {
            login(accessToken);

            // Track KP phone number logged in event
            if (event.detail.phone) {
              publishEvent({
                type: EventType.KP_PHONE_NUMBER_LOGGED_IN,
                phone: event.detail.phone as string,
              });
            }

            // Track KP WhatsApp logged in event
            if (event.detail.loginMethod === "whatsapp") {
              publishEvent({
                type: EventType.KP_WHATSAPP_LOGGED_IN,
                phone: event.detail.phone as string,
              });
            }

            // Track KP Truecaller logged in event
            if (event.detail.loginMethod === "truecaller") {
              publishEvent({
                type: EventType.KP_TRUECALLER_LOGGED_IN,
                phone: event.detail.phone as string,
              });
            }

            // Track KP Shopify logged in event
            if (event.detail.loginMethod === "shopify") {
              publishEvent({
                type: EventType.KP_SHOPIFY_LOGGED_IN,
                userId: event.detail.userId as string,
              });
            }
          }
        } catch (error) {
          console.error("Error during KwikPass login:", error);
        }
      }
    },
    [login],
  );

  // Effect hook to add event listeners once the script is loaded
  useEffect(() => {
    if (isKwikPassScriptLoaded && typeof window !== "undefined") {
      window.addEventListener("kp_data_sent", handleUserLogin); // User login event

      return () => {
        window.removeEventListener("kp_data_sent", handleUserLogin);
      };
    }
  }, [isKwikPassScriptLoaded, handleUserLogin]);

  // Effect for DOM updates
  useEffect(() => {
    if (!(window && isKwikPassScriptLoaded)) return;

    if (window.handleDOMUpdate) {
      window.handleDOMUpdate();
    }

    const handleDomUpdate = () => {
      if (window.handleDOMUpdate) {
        window.handleDOMUpdate();
      }
    };

    window.addEventListener("update-dom", handleDomUpdate);

    return () => {
      window.removeEventListener("update-dom", handleDomUpdate);
    };
  }, [isKwikPassScriptLoaded]);

  // Effect hook to send page view event whenever the page changes
  useEffect(() => {
    if (isKwikPassScriptLoaded && typeof window !== "undefined") {
      // Send page view event (you can expand this functionality as needed)
      if (!pathname.startsWith("/products")) {
        dispatchOtherPageViewEvent({
          cart_id: CartStorage.getCartToken() || "",
        });
      }
    }
  }, [isKwikPassScriptLoaded, pathname]);

  return <KwikPassScript onLoad={onLoad} />;
}
